import { type ReactElement, useMemo, useState } from 'react';
import { Flag, ScopeColumn, ScopeColumns, ScopeSwitcher, UiIcon } from '@vello/web-theme';
import { type OperatorScope, countryOfRegion, regionOfMarket, regionSuffix, scopeAllowed, scopeMenuModel } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useCountryName } from '../i18n/country-name';
import { usePlaceNames } from '../i18n/scope-name';

/** The hover key of the group of cities that have no state (a real region code never looks like this). */
const NO_STATE = '~';

/**
 * The text on the scope pill: the deepest level of the scope - "BR · Paracatu" for a city, "BR · MG" for a state (the code's suffix), the
 * country code for a country, the global label for the platform.
 */
export function scopeLabel(scope: OperatorScope, globalLabel: string, cityName?: (city: string) => string): string {
  if (scope.city !== undefined) return `${scope.country ?? '—'} · ${cityName?.(scope.city) ?? scope.city}`;
  if (scope.region !== undefined) return `${scope.country ?? countryOfRegion(scope.region)} · ${regionSuffix(scope.region)}`;
  if (scope.country !== undefined) return scope.country;
  return globalLabel;
}

/**
 * The Admin's scope selector (Global > Country > State > City): the A08 popover with up to three columns - countries | states | cities.
 * Without any state in the operator's reach it is the two-column country | city menu of old; without any city, one column. A country with no
 * state level lists its cities in the second column; a city whose market has no listed state sits under "No state" so it stays reachable.
 * Hover (or click) a row to open the next column; a row selects only through its "whole ..." entry or when nothing sits below it.
 */
export function AdminScopeSwitcher(): ReactElement {
  const { scope, markets, regions, setScope } = useAdmin();
  const { t } = useT();
  const countryName = useCountryName();
  const place = usePlaceNames();
  const globalLabel = t('header.scope.global');
  const model = useMemo(() => scopeMenuModel(scope.allowed, markets, regions ?? []), [scope.allowed, markets, regions]);
  const marketName = useMemo(() => new Map(markets.map((m) => [m.id, m.name])), [markets]);
  const nameOf = (city: string): string => marketName.get(city) ?? city;

  const [hoverCountryKey, setHoverCountry] = useState<string | undefined>(undefined);
  const [hoverStateKey, setHoverState] = useState<string | undefined>(undefined);
  const current = scope.current;
  const currentRegion = current.region ?? (current.city !== undefined ? regionOfMarket(current.city, markets) : undefined);

  const hoverCountry = hoverCountryKey ?? current.country;
  const country = model.countries.find((c) => c.country === hoverCountry);
  // The state column shows the hovered state, else (while the pointer is on the same country as the scope) the state the scope is in.
  const stateKey = hoverStateKey ?? (hoverCountry === current.country && (current.region !== undefined || current.city !== undefined) ? (currentRegion ?? NO_STATE) : undefined);
  const stateGroup = country?.states.find((s) => (s.region ?? NO_STATE) === stateKey);

  const columns: 1 | 2 | 3 = model.hasStates ? 3 : model.hasCities ? 2 : 1;
  const width = columns === 3 ? 640 : columns === 2 ? 440 : 260;
  const cityRow = (city: string, close: () => void, parentCountry: string): ReactElement => (
    <button key={city} type="button" className="vello-menu__item" role="menuitemradio" aria-checked={current.city === city} onClick={() => { setScope({ country: parentCountry, city }); close(); }}>
      <span className="vello-grow">{nameOf(city)}</span>
      {current.city === city && <UiIcon name="check" size={14} />}
    </button>
  );

  return (
    <ScopeSwitcher label={t('header.scope')} value={scopeLabel(current, globalLabel, nameOf)} width={width}>
      {(close) => (
        <ScopeColumns columns={columns}>
          <ScopeColumn divided={columns > 1}>
            <div className="vello-menu__heading">{t('header.scope')}</div>
            {scopeAllowed(scope, {}) && (
              <button type="button" className="vello-menu__item" role="menuitemradio" aria-checked={current.country === undefined} onMouseEnter={() => { setHoverCountry(undefined); setHoverState(undefined); }} onClick={() => { setScope({}); close(); }}>
                <UiIcon name="globe" size={16} />
                <span className="vello-grow">{globalLabel}</span>
                {current.country === undefined && <UiIcon name="check" size={14} />}
              </button>
            )}
            {model.countries.map((c) => {
              const below = c.states.length > 0 || c.cities.length > 0;
              return (
                <button
                  key={c.country}
                  type="button"
                  className="vello-menu__item"
                  role="menuitemradio"
                  aria-checked={current.country === c.country}
                  onMouseEnter={() => { setHoverCountry(c.country); setHoverState(undefined); }}
                  onClick={() => {
                    if (!below && scopeAllowed(scope, { country: c.country })) { setScope({ country: c.country }); close(); } else { setHoverCountry(c.country); setHoverState(undefined); }
                  }}
                >
                  <Flag code={c.country} size="sm" />
                  <span className="vello-grow">{countryName(c.country)}</span>
                  {current.country === c.country && current.region === undefined && current.city === undefined && <UiIcon name="check" size={14} />}
                  {below && <UiIcon name="chevronRight" size={13} />}
                </button>
              );
            })}
          </ScopeColumn>
          {columns >= 2 && (
            <ScopeColumn divided={columns === 3}>
              <div className="vello-menu__heading">{hoverCountry !== undefined ? `${t(model.hasStates ? 'header.state' : 'header.city')} · ${hoverCountry}` : t(model.hasStates ? 'header.state' : 'header.city')}</div>
              {country === undefined || (country.states.length === 0 && country.cities.length === 0) ? (
                <div className="vello-menu__note vello-admin-a-plain-note">{t('header.scope.hint')}</div>
              ) : (
                <>
                  {/* D358: "whole country" only when a grant covers the country (a state or city grant does not). */}
                  {country.allowed && (
                    <button type="button" className="vello-menu__item" role="menuitemradio" aria-checked={current.country === country.country && current.region === undefined && current.city === undefined} onClick={() => { setScope({ country: country.country }); close(); }}>
                      <UiIcon name="mapPin" size={16} />
                      <span className="vello-grow">{t('header.wholeCountry')}</span>
                    </button>
                  )}
                  {country.states.length === 0
                    ? country.cities.map((city) => cityRow(city, close, country.country))
                    : country.states.map((s) => {
                        const key = s.region ?? NO_STATE;
                        const region = s.region;
                        const hasCities = s.cities.length > 0;
                        const selected = region !== undefined && current.country === country.country && current.region === region && current.city === undefined;
                        return (
                          <button
                            key={key}
                            type="button"
                            className="vello-menu__item"
                            role="menuitemradio"
                            aria-checked={selected}
                            onMouseEnter={() => setHoverState(key)}
                            onClick={() => {
                              if (!hasCities && region !== undefined && s.allowed) { setScope({ country: country.country, region }); close(); } else setHoverState(key);
                            }}
                          >
                            <span className="vello-grow">{region !== undefined ? place.region(region) : t('header.noState')}</span>
                            {selected && <UiIcon name="check" size={14} />}
                            {hasCities && <UiIcon name="chevronRight" size={13} />}
                          </button>
                        );
                      })}
                </>
              )}
            </ScopeColumn>
          )}
          {columns === 3 && (
            <ScopeColumn>
              <div className="vello-menu__heading">{stateGroup !== undefined ? `${t('header.city')} · ${stateGroup.region !== undefined ? regionSuffix(stateGroup.region) : t('header.noState')}` : t('header.city')}</div>
              {stateGroup === undefined || (stateGroup.cities.length === 0 && !(stateGroup.region !== undefined && stateGroup.allowed)) ? (
                <div className="vello-menu__note vello-admin-a-plain-note">{t('header.scope.hint')}</div>
              ) : (
                <>
                  {/* "Whole state" only when a REGION grant (or a wider one) covers the state: a city grant does not. */}
                  {stateGroup.region !== undefined && stateGroup.allowed && country !== undefined && (
                    <button type="button" className="vello-menu__item" role="menuitemradio" aria-checked={current.region === stateGroup.region && current.city === undefined} onClick={() => { setScope({ country: country.country, region: stateGroup.region }); close(); }}>
                      <UiIcon name="mapPin" size={16} />
                      <span className="vello-grow">{t('header.wholeState')}</span>
                    </button>
                  )}
                  {country !== undefined && stateGroup.cities.map((city) => cityRow(city, close, country.country))}
                </>
              )}
            </ScopeColumn>
          )}
        </ScopeColumns>
      )}
    </ScopeSwitcher>
  );
}
